/**
 * ReportBuilder - 3-step modal that demonstrates the full New Report flow with
 * dummy data: pick template -> configure sections + range -> preview + export.
 *
 * In Phase D the same UI is re-pointed at a real backend endpoint.
 */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { CATEGORY_PACKS, ALL_SECTIONS, SECTION_SOURCES, buildSectionData, buildNarrative, buildCSV, computeDateBounds } from "./reportData.v2";
import { showProvenance } from "./env.v2";
import { useFocusTrap } from "../../utils/useFocusTrap";

const RANGE_LABELS = {
  "today":      "Today",
  "this-week":  "This week",
  "this-month": "This month",
  "last-month": "Last month",
  "this-year":  "This year",
  "last-year":  "Last year",
  "till-date":  "Till date",
  "custom":     "Custom range",
};

const RANGE_OPTIONS = [
  { id: "today",      label: "Today" },
  { id: "this-week",  label: "This week" },
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "this-year",  label: "This year" },
  { id: "last-year",  label: "Last year" },
  { id: "till-date",  label: "Till date" },
  { id: "custom",     label: "Custom range" },
];

const STEPS = [
  { id: 1, label: "Pick template" },
  { id: 2, label: "Configure" },
  { id: 3, label: "Preview & export" },
];

function StepHeader({ step }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 0 16px", borderBottom: "1px solid " + T.border.subtle }}>
      {STEPS.map((s, i) => {
        const active = s.id === step;
        const done = s.id < step;
        return (
          <React.Fragment key={s.id}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 24, height: 24, borderRadius: "50%",
                background: active || done ? T.brand.base : "rgba(31,42,63,0.10)",
                color: active || done ? "#FFFFFF" : T.text.muted,
                fontSize: 11, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{done ? "✓" : s.id}</span>
              <span style={{ fontSize: 13, fontWeight: active ? 700 : 600, color: active ? T.text.primary : T.text.muted }}>{s.label}</span>
            </span>
            {i < STEPS.length - 1 && <span style={{ flex: 1, height: 1, backgroundColor: T.border.subtle }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// --- Step 1: pick category pack ---
function StepPickPack({ onPick }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 12 }}>
      {CATEGORY_PACKS.map((p) => (
        <button key={p.id} type="button" onClick={() => onPick(p)} style={{
          display: "flex", alignItems: "flex-start", gap: 14,
          padding: "16px 18px", textAlign: "left", cursor: "pointer", outline: "none",
          backgroundColor: T.surface.card,
          border: "1px solid " + T.border.subtle,
          borderLeft: "4px solid " + p.accent,
          borderRadius: 12,
          fontFamily: T.font.sans,
        }}>
          <span style={{
            width: 38, height: 38, borderRadius: 10,
            backgroundColor: p.accent + "22", color: p.accent,
            fontSize: 16, fontWeight: 800,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>{p.icon}</span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: T.text.primary, marginBottom: 4 }}>{p.title}</span>
            <span style={{ display: "block", fontSize: 12, color: T.text.muted, lineHeight: 1.4 }}>{p.desc}</span>
            <span style={{ display: "block", fontSize: 11, color: T.brand.base, fontWeight: 700, marginTop: 8 }}>
              {p.defaultSections.length} sections - click to use
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

// --- Step 2: configure ---
function todayIso() {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

function StepConfigure({ rangeId, onRangeChange, customRange, onCustomRangeChange, sections, onToggleSection, onMove }) {
  const grouped = {};
  Object.values(ALL_SECTIONS).forEach((s) => {
    if (!grouped[s.group]) grouped[s.group] = [];
    grouped[s.group].push(s);
  });
  const moveUp = (idx) => idx > 0 && onMove(idx, idx - 1);
  const moveDown = (idx) => idx < sections.length - 1 && onMove(idx, idx + 1);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(min(260px, 100%), 1fr) 2fr", gap: 18, alignItems: "start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted, marginBottom: 6 }}>Date range</div>
          <select value={rangeId} onChange={(e) => onRangeChange(e.target.value)} style={{
            width: "100%", padding: "10px 12px",
            border: "1px solid " + T.border.subtle, borderRadius: 8,
            fontSize: 13, fontWeight: 600, color: T.text.primary,
            backgroundColor: T.surface.card, outline: "none", fontFamily: T.font.sans,
          }}>
            {RANGE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          {rangeId === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, padding: 10, border: "1px solid " + T.border.subtle, borderRadius: 8, backgroundColor: T.surface.card }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                From
                <input type="date" value={(customRange && customRange.from) || ""}
                  max={(customRange && customRange.to) || todayIso()}
                  onChange={(e) => onCustomRangeChange({ from: e.target.value, to: (customRange && customRange.to) || todayIso() })}
                  style={dateInputStyle} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                To
                <input type="date" value={(customRange && customRange.to) || todayIso()}
                  min={(customRange && customRange.from) || undefined}
                  max={todayIso()}
                  onChange={(e) => onCustomRangeChange({ from: (customRange && customRange.from) || "", to: e.target.value })}
                  style={dateInputStyle} />
              </label>
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted, marginBottom: 6 }}>Selected order ({sections.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: 8, border: "1px solid " + T.border.subtle, borderRadius: 8, backgroundColor: T.surface.card, minHeight: 120 }}>
            {sections.length === 0 && <div style={{ padding: 12, fontSize: 12, color: T.text.muted, textAlign: "center" }}>No sections selected.</div>}
            {sections.map((id, idx) => {
              const meta = ALL_SECTIONS[id];
              if (!meta) return null;
              return (
                <div key={id} style={{
                  display: "grid", gridTemplateColumns: "1fr auto auto auto",
                  alignItems: "center", gap: 6,
                  padding: "6px 8px", borderRadius: 6,
                  backgroundColor: T.surface.canvas,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{meta.title}</span>
                  <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0} style={ghostBtn(idx === 0)}>▲</button>
                  <button type="button" onClick={() => moveDown(idx)} disabled={idx === sections.length - 1} style={ghostBtn(idx === sections.length - 1)}>▼</button>
                  <button type="button" onClick={() => onToggleSection(id)} style={ghostBtn(false, "#FF4757")}>✕</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted }}>Available sections</div>
        {Object.entries(grouped).map(([group, items]) => (
          <div key={group} style={{ borderTop: "1px solid " + T.border.subtle, paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text.primary, marginBottom: 8 }}>{group}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {items.map((s) => {
                const checked = sections.includes(s.id);
                return (
                  <button key={s.id} type="button" onClick={() => onToggleSection(s.id)} style={{
                    padding: "6px 12px",
                    fontSize: 12, fontWeight: 600,
                    color: checked ? "#FFFFFF" : T.text.primary,
                    background: checked ? T.brand.base : T.surface.card,
                    border: "1px solid " + (checked ? T.brand.base : T.border.subtle),
                    borderRadius: 999, cursor: "pointer", outline: "none",
                    fontFamily: T.font.sans,
                  }}>{checked ? "✓ " : "+ "}{s.title}</button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const dateInputStyle = {
  padding: "8px 10px",
  border: "1px solid " + T.border.subtle,
  borderRadius: 8,
  fontSize: 13, fontFamily: T.font.sans, color: T.text.primary,
  backgroundColor: T.surface.canvas, outline: "none",
};

const ghostBtn = (disabled, color) => ({
  width: 24, height: 24, borderRadius: 6,
  backgroundColor: "transparent",
  border: "1px solid " + T.border.subtle,
  color: color || T.text.secondary,
  fontSize: 11, fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.4 : 1,
  outline: "none",
});

// --- Step 3: preview ---
function PreviewKPI({ d }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(160px, 100%), 1fr))", gap: 10 }}>
      {d.kpis.map((k) => (
        <div key={k.label} style={{ padding: 12, backgroundColor: T.surface.canvas, borderRadius: 8, border: "1px solid " + T.border.subtle }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.label}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text.primary, marginTop: 4 }}>{d.format(k.value)}</div>
          {k.sub && <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>{k.sub}</div>}
        </div>
      ))}
    </div>
  );
}
function PreviewTable({ headers, rows, highlight }) {
  // highlight: array of column indices where the highest value should get bold + bg
  const maxByCol = {};
  if (highlight && highlight.length) {
    highlight.forEach((c) => {
      let max = -Infinity;
      rows.forEach((r) => { const v = r[c]; if (typeof v === "number" && v > max) max = v; });
      maxByCol[c] = max;
    });
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans }}>
        <thead>
          <tr>{headers.map((h) => (
            <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid " + T.border.subtle }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, ci) => {
                const isPeak = highlight && highlight.includes(ci) && c === maxByCol[ci];
                return (
                  <td key={ci} style={{
                    padding: "8px 10px", fontSize: 13,
                    color: T.text.primary,
                    borderBottom: "1px solid " + T.border.subtle,
                    fontWeight: isPeak ? 800 : 500,
                    backgroundColor: isPeak ? "rgba(252,128,25,0.14)" : "transparent",
                  }}>{c}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function PreviewSection({ meta, bounds }) {
  const d = buildSectionData(meta.id);
  const src = SECTION_SOURCES[meta.id] || {};
  const provenance = showProvenance();
  if (!d) return null;
  return (
    <div style={{
      padding: 14, backgroundColor: T.surface.card,
      border: "1px solid " + T.border.subtle, borderLeft: "4px solid " + T.brand.base,
      borderRadius: 10,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>{meta.title}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
          {provenance && (src.tables || []).map((t) => (
            <span key={t} style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
              color: T.text.secondary,
              background: T.surface.canvas,
              border: "1px solid " + T.border.subtle,
              padding: "2px 8px", borderRadius: 999,
              fontFamily: T.font.mono,
            }}>{t}</span>
          ))}
          {bounds && bounds.from && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
              color: T.brand.base,
              background: T.brand.soft,
              padding: "2px 8px", borderRadius: 999,
              fontFamily: T.font.mono,
            }}>{bounds.from} → {bounds.to}</span>
          )}
          {provenance && src.rows != null && (
            <span style={{ fontSize: 10, fontWeight: 600, color: T.text.muted, padding: "2px 4px" }}>
              {src.rows.toLocaleString("en-IN")} rows
            </span>
          )}
        </div>
        {provenance && src.query && (
          <div style={{ fontSize: 11, color: T.text.muted, marginTop: 4, fontFamily: T.font.mono, lineHeight: 1.4 }}>
            {src.query}
          </div>
        )}
      </div>
      {d.kind === "kpi" && <PreviewKPI d={d} />}
      {d.kind === "trend" && <PreviewTable headers={["Period", "Value"]} rows={d.series.map((p) => [p.label, d.format(p.value)])} highlight={[]} />}
      {(d.kind === "hbars" || d.kind === "bars") && <PreviewTable headers={["Label", "Value"]} rows={d.rows.map((r) => [r.label, r.value])} highlight={[1]} />}
      {d.kind === "donut" && (
        <div>
          <div style={{ fontSize: 12, color: T.text.muted, marginBottom: 4 }}>{d.center.label}: <strong style={{ color: T.text.primary }}>{d.format(d.center.value)}</strong></div>
          <PreviewTable headers={["Segment", "Value", "%"]} rows={d.segments.map((s) => {
            const pct = (s.value / d.segments.reduce((sum, x) => sum + x.value, 0)) * 100;
            return [s.label, s.value, pct.toFixed(1) + "%"];
          })} highlight={[1]} />
        </div>
      )}
      {d.kind === "leaderboard" && <PreviewTable headers={["Rank", "Name", d.rightLabel || "Value"]} rows={d.rows.map((r, i) => [i + 1, r.name, r.value])} highlight={[2]} />}
      {d.kind === "funnel" && (() => {
        const max = d.stages[0].value;
        return <PreviewTable
          headers={["Stage", "Count", "% of top"]}
          rows={d.stages.map((s) => [s.label, s.value, ((s.value / max) * 100).toFixed(1) + "%"])}
          highlight={[1]}
        />;
      })()}
    </div>
  );
}

function downloadCSV(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}

function StepPreview({ rangeId, customRange, sections, narrative, setNarrative, packTitle }) {
  const rangeLabel = RANGE_LABELS[rangeId] || rangeId;
  const bounds = computeDateBounds(rangeId, customRange);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Range bounds banner */}
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
        padding: "12px 14px",
        backgroundColor: T.brand.soft,
        border: "1px solid " + T.brand.base,
        borderRadius: 10, fontFamily: T.font.sans,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.brand.base }}>
          Range
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>{rangeLabel}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text.secondary, fontFamily: T.font.mono }}>
          {bounds.from} → {bounds.to}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted }}>· IST · INR · Indian number format</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted }}>{sections.length} sections</span>
      </div>

      <div style={{ padding: 14, backgroundColor: T.surface.card, border: "1px solid " + T.border.subtle, borderLeft: "4px solid #7C4DFF", borderRadius: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Narrative summary</div>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted }}>Auto-generated. Edit freely.</span>
        </div>
        <textarea
          value={narrative.join("\n")}
          onChange={(e) => setNarrative(e.target.value.split("\n"))}
          style={{
            width: "100%", minHeight: 130, resize: "vertical",
            padding: 10, fontFamily: T.font.sans, fontSize: 13,
            border: "1px solid " + T.border.subtle, borderRadius: 8,
            color: T.text.primary, backgroundColor: T.surface.canvas, outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
      {sections.map((id) => {
        const meta = ALL_SECTIONS[id];
        return meta ? <PreviewSection key={id} meta={meta} bounds={bounds} /> : null;
      })}
    </div>
  );
}

const primaryBtn = {
  padding: "10px 18px",
  background: T.brand.base,
  border: "1px solid " + T.brand.base,
  borderRadius: 10,
  color: "#FFFFFF", fontSize: 13, fontWeight: 700,
  cursor: "pointer", outline: "none",
  fontFamily: T.font.sans,
  boxShadow: "0 2px 6px rgba(252,128,25,0.30)",
};
const secondaryBtn = {
  padding: "10px 16px",
  background: T.surface.card,
  border: "1px solid " + T.border.subtle,
  borderRadius: 10,
  color: T.text.primary, fontSize: 13, fontWeight: 600,
  cursor: "pointer", outline: "none",
  fontFamily: T.font.sans,
};

export function ReportBuilder({ onClose, onTemplateSaved, initialRangeId, initialCustomRange }) {
  const [step, setStep] = useState(1);
  const [pack, setPack] = useState(null);
  const [rangeId, setRangeId] = useState(initialRangeId || "this-month");
  const [customRange, setCustomRange] = useState(initialCustomRange || null);
  const [sections, setSections] = useState([]);
  const [narrative, setNarrative] = useState([]);

  const handlePick = (p) => {
    setPack(p);
    setSections(p.defaultSections.slice());
    setStep(2);
  };
  const toggle = (id) => setSections((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const move = (from, to) => setSections((s) => {
    const next = s.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  });
  const goPreview = () => {
    const b = computeDateBounds(rangeId, customRange);
    setNarrative(buildNarrative(sections, (RANGE_LABELS[rangeId] || rangeId) + " (" + b.from + " to " + b.to + ")"));
    setStep(3);
  };
  const saveTemplate = () => {
    const tpl = {
      name: pack ? pack.title : "Custom report",
      sections: sections.slice(),
      rangeId, customRange, narrative: narrative.slice(),
      savedAt: new Date().toISOString(),
    };
    if (onTemplateSaved) onTemplateSaved(tpl);
    alert("Saved as template: " + tpl.name);
  };
  const schedule = () => alert("Scheduled monthly delivery (mock).");
  const handleDownload = () => {
    const bounds = computeDateBounds(rangeId, customRange);
    const csv = buildCSV(sections, RANGE_LABELS[rangeId] || rangeId, narrative, {
      bounds,
      generatedBy: "Jasmin T (CEO)",
      title: pack ? pack.title : "Custom report",
      // STRICT: provenance (DB tables / SQL / row counts) only included in non-production builds.
      includeProvenance: showProvenance(),
    });
    const filename = "fancall-report-" + rangeId + "-" + bounds.from + "-to-" + bounds.to + ".csv";
    downloadCSV(csv, filename);
  };

  const canProceed = sections.length > 0;

  // Modal a11y: focus-trap + Escape-to-close + body scroll-lock while open.
  const modalRef = useRef(null);
  useFocusTrap(modalRef, { active: true, onEscape: onClose });
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div onClick={onClose} role="presentation" style={{
      position: "fixed", inset: 0,
      backgroundColor: "rgba(31,42,63,0.50)",
      zIndex: 100,
      display: "flex", alignItems: "stretch", justifyContent: "center",
      padding: 24,
    }}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={pack ? `New report — ${pack.title}` : "New report"}
        style={{
        flex: 1, maxWidth: 1100, maxHeight: "100%",
        backgroundColor: T.surface.canvas,
        borderRadius: 16,
        boxShadow: "0 20px 64px rgba(31,42,63,0.30)",
        display: "flex", flexDirection: "column",
        fontFamily: T.font.sans,
        overflow: "hidden",
      }}>
        <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 22px", borderBottom: "1px solid " + T.border.subtle,
          backgroundColor: T.surface.card,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>New report</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>
              {pack ? pack.title : "Pick a template"}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: T.surface.canvas, border: "1px solid " + T.border.subtle,
            color: T.text.secondary, fontSize: 18,
            cursor: "pointer", outline: "none",
          }}>✕</button>
        </header>

        <div style={{ padding: "16px 22px 0" }}>
          <StepHeader step={step} />
        </div>

        <div style={{ flex: 1, padding: 22, overflowY: "auto" }}>
          {step === 1 && <StepPickPack onPick={handlePick} />}
          {step === 2 && (
            <StepConfigure
              rangeId={rangeId}
              onRangeChange={setRangeId}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
              sections={sections}
              onToggleSection={toggle}
              onMove={move}
            />
          )}
          {step === 3 && (
            <StepPreview
              rangeId={rangeId}
              customRange={customRange}
              sections={sections}
              narrative={narrative}
              setNarrative={setNarrative}
              packTitle={pack ? pack.title : "Custom report"}
            />
          )}
        </div>

        <footer style={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
          padding: "14px 22px", borderTop: "1px solid " + T.border.subtle,
          backgroundColor: T.surface.card,
        }}>
          <button type="button"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            style={secondaryBtn}>
            {step > 1 ? "← Back" : "Cancel"}
          </button>
          {step < 3 && (
            <span style={{ flex: 1, textAlign: "center", fontSize: 12, fontWeight: 600, color: T.text.muted }}>
              {step === 1 && "Step 1 of 3 - choose a starting point"}
              {step === 2 && sections.length + " section(s) selected - " + (RANGE_LABELS[rangeId] || rangeId)}
            </span>
          )}
          {step === 3 && (
            <>
              <button type="button" onClick={saveTemplate} style={secondaryBtn}>Save as template</button>
              <button type="button" onClick={schedule} style={secondaryBtn}>Schedule monthly</button>
              <span style={{ flex: 1 }} />
            </>
          )}
          {step < 3 ? (
            <button type="button"
              disabled={step === 2 && !canProceed}
              onClick={() => step === 1 ? setStep(2) : goPreview()}
              style={{ ...primaryBtn, opacity: (step === 2 && !canProceed) ? 0.5 : 1, cursor: (step === 2 && !canProceed) ? "not-allowed" : "pointer" }}>
              {step === 1 ? "Skip - blank canvas →" : "Preview →"}
            </button>
          ) : (
            <button type="button" onClick={handleDownload} style={primaryBtn}>↓ Download CSV</button>
          )}
        </footer>
      </div>
    </div>
  );
}
